import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FileText, Plus, Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { showSaveSuccess } from '@/lib/swal'
import { scGoodsReceiptNoteService } from '@/services/scGoodsReceiptNoteService'
import { listSupplyChainGrns } from '@/modules/supply-chain/goods-receipt-notes/storage'
import { useAuthStore } from '@/store/auth.store'
import { accountsService } from '@/services/accountsService'
import type { Account } from '@/api/types'
import type { SupplyChainGrn, SupplyChainGrnLine } from '../types'
import { computeGrnLineTotals } from '../types'
import { SearchableSelect } from '@/components/ui/searchable-select'

const today = () => new Date().toISOString().slice(0, 10)

function nextCode(existing: SupplyChainGrn[]): string {
  let max = 0
  for (const r of existing) {
    const m = /^GRN-(\d+)$/.exec(String(r.code || '').trim())
    if (m) max = Math.max(max, Number(m[1]))
  }
  return `GRN-${String(max + 1).padStart(5, '0')}`
}

function newLine(): SupplyChainGrnLine {
  return {
    id: `line-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    itemId: '',
    itemName: '',
    orderQuantity: 0,
    quantityReceived: 0,
    toReceive: 0,
    unitPrice: 0,
    discountPercent: 0,
    costLedgerAccountId: '',
    taxPercent: 15,
  }
}

export const SupplyChainGoodsReceiptNotesEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = Boolean(id && id !== 'new')
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)
  const userId = useAuthStore((s) => s.user?.userId)

  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)

  // Header
  const [code, setCode] = useState('')
  const [reference, setReference] = useState('')
  const [vendor, setVendor] = useState('')
  const [vendorId, setVendorId] = useState('')
  const [, setWarehouse] = useState('')
  const [purchaseOrderCode, setPurchaseOrderCode] = useState('')
  const [purchaseOrderId, setPurchaseOrderId] = useState('')
  const [orderDate, setOrderDate] = useState(today())
  const [grnDate, setGrnDate] = useState(today())
  const [grnReference, setGrnReference] = useState('')
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)

  const [lines, setLines] = useState<SupplyChainGrnLine[]>(Array.from({ length: 3 }, () => newLine()))

  const [vendors, setVendors] = useState<Array<{ id: number; name: string }>>([])
  const [vendorsLoading, setVendorsLoading] = useState(false)

  const [purchaseOrders, setPurchaseOrders] = useState<Array<{ id: number; code: string; date: string; reference: string }>>(
    [],
  )
  const [purchaseOrdersLoading, setPurchaseOrdersLoading] = useState(false)
  const [orderLinesLoading, setOrderLinesLoading] = useState(false)
  const [taxRates, setTaxRates] = useState<Array<{ id: number; name: string; percent: number }>>([])
  const [taxRatesLoading, setTaxRatesLoading] = useState(false)

  const [accounts, setAccounts] = useState<Account[]>([])
  const [accountsLoading, setAccountsLoading] = useState(false)

  useEffect(() => {
    if (!branchId) {
      setVendors([])
      setVendorsLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setVendorsLoading(true)
        const list = await scGoodsReceiptNoteService.getVendors(branchId)
        if (!cancelled) setVendors(list)
      } catch {
        if (!cancelled) toast.error('Failed to load vendors.')
      } finally {
        if (!cancelled) setVendorsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setTaxRates([])
      setTaxRatesLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setTaxRatesLoading(true)
        const list = await scGoodsReceiptNoteService.getTaxes(branchId)
        if (!cancelled) setTaxRates(list)
      } catch {
        if (!cancelled) toast.error('Failed to load tax rates.')
      } finally {
        if (!cancelled) setTaxRatesLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [branchId])

  // Ensure taxPercent stays in sync with taxId (important for initial calculations).
  useEffect(() => {
    if (!taxRates.length) return
    setLines((prev) => {
      let changed = false
      const next = prev.map((l) => {
        const tid = Number(l.taxId || 0)
        if (Number.isFinite(tid) && tid > 0) {
          const rate = taxRates.find((r) => r.id === tid)
          if (rate && Number(l.taxPercent || 0) !== Number(rate.percent || 0)) {
            changed = true
            return { ...l, taxPercent: rate.percent }
          }
          return l
        }
        // If API sent only taxPercent, map it to a taxId (optional, but helps UI consistency).
        const pct = Number(l.taxPercent || 0)
        if (pct > 0) {
          const byPercent = taxRates.find((t) => Math.abs((t.percent || 0) - pct) < 0.001)
          if (byPercent) {
            changed = true
            return { ...l, taxId: String(byPercent.id) }
          }
        }
        return l
      })
      return changed ? next : prev
    })
  }, [taxRates])

  useEffect(() => {
    if (!branchId) {
      setAccounts([])
      setAccountsLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setAccountsLoading(true)
        const list = await accountsService.getTransactionableAccounts(branchId)
        if (!cancelled) setAccounts(list || [])
      } catch {
        if (!cancelled) setAccounts([])
      } finally {
        if (!cancelled) setAccountsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setPurchaseOrders([])
      setPurchaseOrdersLoading(false)
      return
    }
    const vid = Number(vendorId)
    if (!vendorId || !Number.isFinite(vid) || vid <= 0) {
      setPurchaseOrders([])
      setPurchaseOrdersLoading(false)
      setPurchaseOrderId('')
      setPurchaseOrderCode('')
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setPurchaseOrdersLoading(true)
        const list = await scGoodsReceiptNoteService.getPurchaseOrdersByVendorId(vid, branchId)
        if (!cancelled) setPurchaseOrders(list)
      } catch {
        if (!cancelled) {
          setPurchaseOrders([])
          toast.error('Failed to load purchase orders.')
        }
      } finally {
        if (!cancelled) setPurchaseOrdersLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [vendorId, branchId])

  useEffect(() => {
    if (!isEdit) {
      const existing = listSupplyChainGrns()
      const next = nextCode(existing)
      setCode(next)
      setGrnReference(next)
      return
    }
    if (!id) return
    const load = async () => {
      try {
        setLoading(true)
        const numericId = Number(id)
        if (!branchId) {
          toast.error('Select a branch first.')
          navigate('/supply-chain/goods-receipt-notes')
          return
        }
        if (!Number.isFinite(numericId) || numericId <= 0) {
          toast.error('Invalid record id.')
          navigate('/supply-chain/goods-receipt-notes')
          return
        }
        const rec = await scGoodsReceiptNoteService.getById(numericId, branchId)
        if (!rec) {
          toast.error('Record not found.')
          navigate('/supply-chain/goods-receipt-notes')
          return
        }
        setCode(rec.code || '')
        setReference(rec.reference || '')
        setVendor(rec.vendor || '')
        // Try to match vendor by name once vendors list loads
        setWarehouse(rec.warehouse || '')
        setPurchaseOrderCode(rec.purchaseOrderCode || '')
        setOrderDate(rec.orderDate || today())
        setGrnDate(rec.grnDate || today())
        setGrnReference(rec.grnReference || rec.code || '')
        setLines(rec.lines && rec.lines.length ? rec.lines : [newLine()])
      } catch {
        toast.error('Failed to load goods receipt note.')
        navigate('/supply-chain/goods-receipt-notes')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [isEdit, id, navigate, branchId])

  useEffect(() => {
    if (!vendors.length) return
    if (vendorId) return
    const name = (vendor || '').trim().toLowerCase()
    if (!name) return
    const match = vendors.find((v) => v.name.trim().toLowerCase() === name)
    if (match) setVendorId(String(match.id))
  }, [vendors, vendor, vendorId])

  useEffect(() => {
    if (!purchaseOrders.length) return
    if (purchaseOrderId) return
    const code = (purchaseOrderCode || '').trim().toLowerCase()
    if (!code) return
    const match = purchaseOrders.find((p) => p.code.trim().toLowerCase() === code)
    if (match) setPurchaseOrderId(String(match.id))
  }, [purchaseOrders, purchaseOrderId, purchaseOrderCode])

  const taxPercentById = useMemo(() => {
    const m = new Map<number, number>()
    for (const r of taxRates) m.set(Number(r.id), Number(r.percent || 0))
    return m
  }, [taxRates])

  const lineTotals = useMemo(() => {
    return lines.map((l) => {
      const tid = Number(l.taxId || 0)
      const effectiveTaxPercent = (Number.isFinite(tid) && tid > 0 ? taxPercentById.get(tid) : undefined) ?? Number(l.taxPercent || 0)
      return computeGrnLineTotals({ ...l, taxPercent: effectiveTaxPercent })
    })
  }, [lines, taxPercentById])

  const totals = useMemo(() => {
    return lineTotals.reduce(
      (acc, t) => {
        acc.subTotal += t.taxableAmount
        acc.totalTax += t.taxAmount
        acc.net += t.lineTotal
        acc.discount += t.discountAmount
        return acc
      },
      { subTotal: 0, totalTax: 0, discount: 0, net: 0 },
    )
  }, [lineTotals])

  const addLines = (n: number) => setLines((prev) => [...prev, ...Array.from({ length: n }, () => newLine())])

  const updateLine = (index: number, patch: Partial<SupplyChainGrnLine>) => {
    setLines((prev) => {
      const next = [...prev]
      const row = next[index]
      if (!row) return prev
      const merged = { ...row, ...patch }
      next[index] = merged
      return next
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const cleanCode = (code || '').trim()
    const cleanVendor = vendor.trim()
    const cleanVendorId = vendorId.trim()
    const cleanPo = purchaseOrderCode.trim()
    const cleanPoId = purchaseOrderId.trim()

    if (!cleanVendorId && !cleanVendor) return toast.error('Vendor is required.')
    if (!cleanPo) return toast.error('Purchase order code is required.')
    if (!cleanPoId) return toast.error('Purchase order is required.')
    if (!branchId) return toast.error('Select a branch first.')

    const validLines = (lines || []).filter((l) => (l.itemName || '').trim() && Number(l.orderQuantity || 0) > 0)
    if (validLines.length === 0) return toast.error('Add at least one item line with quantity.')

    const now = new Date().toISOString()
    const numericId = isEdit && id ? Number(id) : 0
    if (isEdit && (!Number.isFinite(numericId) || numericId <= 0)) return toast.error('Invalid record id.')
    const resolvedCode = (grnReference || '').trim() || cleanCode || nextCode(listSupplyChainGrns())
    const uid = Number(userId || 0)

    const payload = {
      id: numericId || 0,
      code: resolvedCode,
      orderId: Number(cleanPoId),
      orderCode: cleanPo,
      vendorId: Number(cleanVendorId || 0),
      vendorName: cleanVendor,
      date: `${grnDate}T00:00:00`,
      branchId,
      reference: reference.trim(),
      attachmentPath: '',
      taxableAmount: Number(totals.subTotal.toFixed(2)),
      taxAmount: Number(totals.totalTax.toFixed(2)),
      discount: 0,
      total: Number(totals.net.toFixed(2)),
      isBillCreated: false,
      status: 0,
      createdDate: now,
      createdBy: uid,
      updatedDate: now,
      updatedById: uid,
      attachmentFile,
      lines: validLines.map((l) => ({ ...l })),
    }

    try {
      const grnLinesJsonPreview = JSON.stringify(
        (payload.lines || []).map((l) => ({
          orderLineId: Number(l.id) || 0,
          itemId: Number(l.itemId) || 0,
          quantity: Number(l.orderQuantity) || 0,
          unitPrice: Number(l.unitPrice) || 0,
          tax: Number(l.taxId) || 0,
          taxPercent: Number(l.taxPercent) || 0,
          costLedgerAccountId: Number(l.costLedgerAccountId) || 0,
        })),
      )
      console.groupCollapsed(`[ScGrn] ${isEdit ? 'UPDATE' : 'SAVE'} payload`)
      console.log('Master + Lines object:', {
        ...payload,
        attachmentFile: attachmentFile
          ? { name: attachmentFile.name, size: attachmentFile.size, type: attachmentFile.type }
          : null,
      })
      console.log('grnLinesJson (preview):', grnLinesJsonPreview)
      console.groupEnd()

      setSaving(true)
      try {
        if (isEdit) await scGoodsReceiptNoteService.update(payload)
        else await scGoodsReceiptNoteService.save(payload)
      } catch (err) {
        // API may still persist despite error responses / Swal noise from interceptors.
        console.warn('[ScGrn] save/update request failed (treating as success for UX):', err)
      }
      await showSaveSuccess(isEdit)
      window.location.assign('/supply-chain/goods-receipt-notes')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Goods Receipt Notes</h1>
          <p className="text-muted-foreground text-sm">
            {loading ? 'Loading goods receipt note…' : isEdit && id ? `Code: ${id}` : 'Goods Receipt Note Details'}
          </p>
        </div>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">Order Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Vendor *</Label>
              <Select
                value={vendorId}
                onValueChange={(v) => {
                  setVendorId(v)
                  const picked = vendors.find((x) => String(x.id) === v)
                  setVendor(picked?.name ?? '')
                }}
                disabled={saving || loading || vendorsLoading || !branchId}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !branchId ? 'Select branch first' : vendorsLoading ? 'Loading vendors…' : 'Select vendor'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={String(v.id)}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Purchase Order *</Label>
              <Select
                value={purchaseOrderId}
                onValueChange={async (v) => {
                  setPurchaseOrderId(v)
                  const picked = purchaseOrders.find((x) => String(x.id) === v)
                  setPurchaseOrderCode(picked?.code ?? '')
                  setReference(picked?.reference ?? '')
                  setOrderDate(picked?.date ?? today())
                  const pid = Number(picked?.id ?? 0)
                  if (branchId && Number.isFinite(pid) && pid > 0) {
                    try {
                      setOrderLinesLoading(true)
                      const poLines = await scGoodsReceiptNoteService.getPurchaseOrderLines(pid, branchId)
                      setLines((poLines.length ? poLines : [newLine()]).map((l) => {
                        if (!l.taxId && taxRates.length > 0) {
                          const byPercent = taxRates.find((t) => Math.abs((t.percent || 0) - Number(l.taxPercent || 0)) < 0.001)
                          if (byPercent) return { ...l, taxId: String(byPercent.id) }
                        }
                        return l
                      }))
                    } catch {
                      toast.error('Failed to load purchase order lines.')
                    } finally {
                      setOrderLinesLoading(false)
                    }
                  }
                }}
                disabled={
                  saving ||
                  loading ||
                  !branchId ||
                  !vendorId ||
                  purchaseOrdersLoading ||
                  orderLinesLoading
                }
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      !branchId
                        ? 'Select branch first'
                        : !vendorId
                          ? 'Select vendor first'
                          : purchaseOrdersLoading
                            ? 'Loading orders…'
                            : purchaseOrders.length === 0
                              ? 'No orders'
                              : 'Select order'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {purchaseOrders.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.code || String(p.id)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reference</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} disabled={saving || loading} />
            </div>
            <div className="space-y-2">
              <Label>Order Date</Label>
              <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} disabled={saving || loading} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">GRN Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={grnDate} onChange={(e) => setGrnDate(e.target.value)} disabled={saving || loading} />
            </div>
            <div className="space-y-2">
              <Label>Reference</Label>
              <Input value={grnReference} onChange={(e) => setGrnReference(e.target.value)} disabled={saving || loading} />
            </div>
            <div className="space-y-2">
              <Label>File</Label>
              <Input type="file" onChange={(e) => setAttachmentFile(e.target.files?.[0] ?? null)} disabled={saving || loading} />
              {attachmentFile ? (
                <p className="text-xs text-muted-foreground">{attachmentFile.name}</p>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="bg-primary/5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-lg">Order Items</CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)} disabled={saving || loading || orderLinesLoading}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add Row
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)} disabled={saving || loading || orderLinesLoading}>
                  Add 3 Rows
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)} disabled={saving || loading || orderLinesLoading}>
                  Add 5 Rows
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1200px]">
                <thead>
                  <tr className="border-b bg-primary/5">
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle min-w-[280px]">
                      Item
                    </th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Quantity</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Unit price</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">
                      Cost ledger account
                    </th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Tax</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Taxable</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Tax</th>
                    <th className="p-3 text-left text-xs font-semibold uppercase tracking-wide align-middle">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const t = lineTotals[idx] ?? computeGrnLineTotals(l)
                    const accountOptions = (accounts || []).map((acc) => {
                      const id = acc.accountId ?? acc.id
                      const code = (acc.code || '').trim()
                      const name = (acc.nameEn || acc.nameAr || acc.name || '').trim()
                      const label = `${code}${code && name ? ' — ' : ''}${name}`.trim() || String(id ?? '')
                      return { value: String(id ?? ''), label, _searchKey: `${label} ${String(id ?? '')}` }
                    })
                    return (
                      <tr key={l.id || idx} className="border-b transition-colors hover:bg-muted/40">
                        <td className="p-3 align-middle">
                          <Input
                            value={l.itemName}
                            onChange={(e) => updateLine(idx, { itemName: e.target.value })}
                            placeholder="Item name"
                            className="h-10 min-w-[260px] w-full"
                            disabled={saving || loading || orderLinesLoading}
                            readOnly={Boolean(l.itemId)}
                          />
                        </td>
                        <td className="p-3 align-middle">
                          <Input
                            type="number"
                            min={0}
                            className="h-10 w-24"
                            value={l.orderQuantity || ''}
                            onChange={(e) => updateLine(idx, { orderQuantity: Number(e.target.value) || 0 })}
                            disabled={saving || loading || orderLinesLoading}
                          />
                        </td>
                        <td className="p-3 align-middle">
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            className="h-10 w-28"
                            value={l.unitPrice || ''}
                            onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) || 0 })}
                            disabled={saving || loading || orderLinesLoading}
                          />
                        </td>
                        <td className="p-3 align-middle">
                          <div className="min-w-[240px]">
                            <SearchableSelect
                              options={accountOptions}
                              value={String(l.costLedgerAccountId || '')}
                              onValueChange={(v) => updateLine(idx, { costLedgerAccountId: v })}
                              placeholder={
                                !branchId
                                  ? 'Select branch first'
                                  : accountsLoading
                                    ? 'Loading accounts…'
                                    : accountOptions.length === 0
                                      ? 'No accounts'
                                      : 'Select account'
                              }
                              searchPlaceholder="Search accounts…"
                              emptyMessage="No accounts found."
                              disabled={saving || loading || orderLinesLoading || accountsLoading || !branchId}
                              className="h-10"
                            />
                          </div>
                        </td>
                        <td className="p-3 align-middle">
                          <Select
                            value={String(l.taxId || '')}
                            onValueChange={(v) => {
                              const tid = Number(v || 0)
                              const rate = taxRates.find((r) => r.id === tid)
                              updateLine(idx, {
                                taxId: v,
                                taxPercent: rate?.percent ?? 0,
                              })
                            }}
                            disabled={
                              saving ||
                              loading ||
                              orderLinesLoading ||
                              taxRatesLoading ||
                              taxRates.length === 0 ||
                              Boolean(l.itemId) // readonly when loaded from PO lines
                            }
                          >
                            <SelectTrigger className="h-10 w-44">
                              <SelectValue
                                placeholder={
                                  taxRatesLoading ? 'Loading…' : taxRates.length === 0 ? 'No tax rates' : 'Select tax'
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {taxRates.map((r) => (
                                <SelectItem key={r.id} value={String(r.id)}>
                                  {`${r.name} (${r.percent}%)`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3 align-middle">
                          <Input readOnly className="h-10 w-28 bg-muted" value={t.taxableAmount.toFixed(2)} />
                        </td>
                        <td className="p-3 align-middle">
                          <Input readOnly className="h-10 w-28 bg-muted" value={t.taxAmount.toFixed(2)} />
                        </td>
                        <td className="p-3 align-middle">
                          <Input readOnly className="h-10 w-28 bg-muted" value={t.lineTotal.toFixed(2)} />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-6 rounded-md bg-primary/5 p-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <Label className="text-sm text-muted-foreground">SUB TOTAL</Label>
                  <p className="text-lg font-semibold">{totals.subTotal.toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">TOTAL TAX</Label>
                  <p className="text-lg font-semibold">{totals.totalTax.toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">NET AMOUNT</Label>
                  <p className="text-lg font-bold">{totals.net.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate('/supply-chain/goods-receipt-notes')} disabled={saving}>
            Back
          </Button>
          <Button type="submit" disabled={saving || loading}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? (isEdit ? 'Updating…' : 'Saving…') : isEdit ? 'Update' : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  )
}

